import { isArray } from '@datorama/akita';

export type QueryFilterNew<T> = {
    [Property in keyof T]?: T[Property] extends Array<infer U>
        ? QueryFilterNew<U>
        : T[Property] extends object
        ? Criteria<T[Property]>
        : Criteria<T[Property]>;
};

type NestedKeyOfInner<TObj extends object> = {
    [TKey in keyof TObj & (string | number)]: TObj[TKey] extends object
        ? NestedKeyOfHandleValue<TObj[TKey], `['${TKey}']` | `.${TKey}`>
        : `['${TKey}']` | `.${TKey}`;
}[keyof TObj & (string | number)];

type NestedKeyOfHandleValue<TValue, Text extends string> = TValue extends any[]
    ? `${Text}[number]`
    : TValue extends object
    ? Text | `${Text}${NestedKeyOfInner<TValue>}`
    : Text;

export type InferFilter<T, O = {}> = T extends object
    ? {
          [K in keyof T]?: T[K] extends object
              ? K extends keyof T
                  ? InferFilter<T[K], T>
                  : never
              : K extends keyof T & string
              ? Criteria<ValueAtPath<T, K>, T>
              : never;
      }
    : never;

type ValueAtPath<T, Path extends string> = Path extends keyof T
    ? T[Path]
    : Path extends `${infer Key}.${infer Rest}`
    ? Key extends keyof T
        ? ValueAtPath<T[Key], Rest>
        : never
    : never;

/*type NestedKeyOfInner<TObj extends object> = {
    [TKey in keyof TObj & (string | number)]: TObj[TKey] extends object
        ? NestedKeyOfHandleValue<TObj[TKey], `['${TKey}']` | `.${TKey}`>
        : `['${TKey}']` | `.${TKey}`;
}[keyof TObj & (string | number)];

type NestedKeyOfHandleValue<TValue, Text extends string> = TValue extends any[]
    ? `${Text}[number]`
    : TValue extends object
    ? Text | `${Text}${NestedKeyOfInner<TValue>}`
    : Text;

export type InferFilter<T> = T extends object
    ? {
          [K in keyof T]?: T[K] extends object
              ? K extends keyof T
                  ? InferFilter<T[K]>
                  : never
              : K extends keyof T & string
              ? Criteria<ValueAtPath<T, K>> | NestedKeyFilter<T, K>
              : never;
      }
    : never;

type NestedKeyFilter<T, K extends string> = K extends keyof T
    ? T[K] extends object
        ? K extends keyof T
            ? Criteria<ValueAtPath<T, K>>
            : never
        : K extends keyof T
        ? Criteria<ValueAtPath<T, K>>
        : never
    : K extends `${infer FirstKey}.${infer Rest}`
    ? FirstKey extends keyof T
        ? NestedKeyFilter<T[FirstKey], Rest>
        : never
    : never;

type ValueAtPath<T, Path extends string> = Path extends keyof T
    ? T[Path]
    : Path extends `${infer Key}.${infer Rest}`
    ? Key extends keyof T
        ? ValueAtPath<T[Key], Rest>
        : never
    : never;
    */

export interface Criteria<T = any, O = any> {
    lt?: number | Date; // less than
    lte?: number | Date; // less than or equal to
    gt?: number | Date; // greater than
    gte?: number | Date; // greater than or equal to
    eq?: T; // equal to
    ne?: T; // not equal to
    in?: Array<T> | T; // in array
    nin?: Array<T> | T; // not in array
    qs?: string; // query string
    qsc?: string; // query string case sensitive
    fn?: (value: T, object: O) => boolean; // custom function
}

export interface QueryFilters {
    [key: string]: Criteria;
}

type FilterOperators = keyof Criteria;

/** Check filters for a match
 * @param value - Value to check
 * @param rules - Rules to check against
 * @param object - Optional object to pass to custom function
 */
export const checkFilter = <T, O = any>(
    value: string | number,
    rules: Criteria<T>,
    object?: O
): boolean => {
    return !Object.entries(rules).find(([operator, criteria]) => {
        if (operator === 'fn') {
            return !(criteria as (value: string | number, object: O) => boolean)(
                value,
                object
            );
        } else if (operator === 'gt') {
            return !(value > criteria);
        } else if (operator === 'gte') {
            return !(value >= criteria);
        } else if (operator === 'lt') {
            return !(value < criteria);
        } else if (operator === 'lte') {
            return !(value <= criteria);
        } else if (operator === 'eq') {
            return !(value === criteria);
        } else if (operator === 'ne') {
            return !(value !== criteria);
        } else if (operator === 'in') {
            if (Array.isArray(value)) {
                return !(value.indexOf(criteria) >= 0);
            }
            return !(criteria.indexOf(value) >= 0);
        } else if (operator === 'nin') {
            if (Array.isArray(value)) {
                return !(value.indexOf(criteria) < 0);
            }
            return !(criteria.indexOf(value) < 0);
        } else if (operator === 'qs') {
            return !(
                !criteria ||
                (typeof value === 'string' &&
                    value.toLowerCase().indexOf(criteria.toLowerCase()) >= 0)
            );
        } else if (operator === 'qsc' && typeof value === 'string') {
            return !(!criteria || value.indexOf(criteria) >= 0);
        }
    });
};

const hasNestedKeys = (obj: any): boolean => {
    for (let key in obj) {
        if (
            obj.hasOwnProperty(key) &&
            typeof obj[key] === 'object' &&
            !isArray(obj[key])
        ) {
            return true;
        }
    }
    return false;
};

export const flattenNestedObject = (filters: any) => {
    const result = {};
    function flatten(obj: any, prefix: string = '') {
        for (let key in obj) {
            if (hasNestedKeys(obj[key])) {
                flatten(obj[key], `${prefix ? `${prefix}.` : ''}${key}`);
            } else {
                result[`${prefix ? `${prefix}.` : ''}${key}`] = obj[key];
            }
        }
    }
    flatten(filters);
    return result;
};

// TODO: move
export interface QueryFilterGroupBy<K = string> {
    key: K;
    valueModifier?: (value: any, entity?: any) => any;
    name?: string;
    subgroup?: QueryFilterGroupBy<K>;
}
